import {
  Group,
  Container,
  Grid,
  TextInput,
  Button,
  Alert,
  Select,
  Stack,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useLocation } from "wouter";
import { createUser, sendUserInvitation } from "../api";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useStore } from "../store";

// Most privileged first. Same order and rule as the server (NBoilerplate.Web.TenantRoles): a
// caller can give only roles below their own; GlobalAdmin can give any tenant role.
const TENANT_ROLES = ["TenantOwner", "TenantAdmin", "TenantUser"];
const ROLE_ORDER = ["GlobalAdmin", "MarketplacePartner", ...TENANT_ROLES];

export function grantableRoles(callerRole) {
  if (callerRole === "GlobalAdmin") return TENANT_ROLES;
  const callerLevel = ROLE_ORDER.indexOf(callerRole);
  if (callerLevel < 0) return [];
  return TENANT_ROLES.filter((r) => ROLE_ORDER.indexOf(r) > callerLevel);
}

export default function UserAdd({ realm }) {
  const [isLoading, setIsLoading] = useState(false);
  const [, setLocation] = useLocation();
  const { t } = useTranslation("Users");
  const callerRole = useStore((state) => state.profile?.role);
  const roles = grantableRoles(callerRole);

  const addUserHandler = (data) => {
    setIsLoading(true);
    createUser(data)
      .then((response) => {
        // The server answers without a userId when the address already has an account (it does
        // not say whose). Without this the form just stopped, with no message.
        if (!response?.userId) {
          setIsLoading(false);
          notifications.show({
            color: "orange",
            autoClose: 8000,
            title: t("user_exists", "This address already has an account"),
            message: t(
              "user_exists_message",
              "This person has an account already, possibly in another workspace. Ask them to log in, or use a different address.",
            ),
          });
          return;
        }
        // setLocation(`/users/${response.id}`); tbd redirect to details and show pending invites there
        sendUserInvitation(response.userId)
          .then(() => {
            setLocation("/users");
            notifications.show({
              color: "green",
              autoClose: 3000,
              title: t("invitation_sent"),
            });
          })
          .finally(() => {
            setIsLoading(false);
          });
      })
      .catch((error) => {
        setIsLoading(false);
      });
  };

  const form = useForm({
    mode: "uncontrolled",
    initialValues: {
      role: "TenantUser",
      realm: realm,
      email: "",
      firstName: "",
      lastName: "",
    },
    validate: {
      email: (val) =>
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(val)
          ? null
          : "Invalid email",
    },
  });

  return (
    <Container size="xs" px={0}>
      <Alert mb="md">
        {t("invite_user_info")}
        {/* TBD: Limit domain to same
        domain as tenant (tenant config, multiple domains) TBD: Limit seats */}
      </Alert>
      <Grid justify="space-between" gap="2px">
        <Grid.Col span={12}>
          <form onSubmit={form.onSubmit(addUserHandler)}>
            <Stack gap="sm">
              <TextInput
                {...form.getInputProps("firstName")}
                key={form.key("firstName")}
                label={t("first_name")}
              />
              <TextInput
                {...form.getInputProps("lastName")}
                key={form.key("lastName")}
                label={t("last_name")}
              />
              <TextInput
                {...form.getInputProps("email")}
                key={form.key("email")}
                label={t("email")}
                withAsterisk
              />
              <Select
                label={t("role")}
                data={roles}
                {...form.getInputProps("role")}
                key={form.key("role")}
              />

              <Group justify="flex-end">
                <Button loading={isLoading} type="submit" mt="md">
                  {t("invite_user")}
                </Button>
              </Group>
            </Stack>
          </form>
        </Grid.Col>
      </Grid>
    </Container>
  );
}
